<template>
  <v-skeleton-loader :loading="loading" type="table">
    <v-chip-group v-model="selection" value="any">
      <v-chip value="pending" filter outlined>{{ $t("pendingOrders") }}</v-chip>
      <v-chip value="confirmed" filter outlined color="success">{{
        $t("confirmedOrders")
      }}</v-chip>
      <v-chip value="rejected" filter outlined color="error">{{
        $t("rejectedOrders")
      }}</v-chip>
      <v-chip value="completed" filter outlined color="primary">{{
        $t("completedOrders")
      }}</v-chip>
    </v-chip-group>
    <v-data-table
      :headers="headers"
      :header-props="{ sortByText: $t('sortBy') }"
      :items="items"
      :single-expand="true"
      :expanded.sync="expanded"
      item-key="_id"
      show-expand
      sort-by="created"
      :search="selection"
      :custom-filter="customFilter"
      :sort-desc="true"
      @click:row="
        (item, slot) =>
          expanded.length === 1 && expanded[0]._id === item._id
            ? (expanded = [])
            : (expanded = [item])
      "
      disable-pagination
      hide-default-footer
    >
      <template v-slot:item.id="{ item }">
        <v-tooltip v-if="item.preorder" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" small class="mr-1"
              >mdi-clock-fast</v-icon
            >
          </template>
          <span>{{ $t("preOrder") }}</span>
        </v-tooltip>
        {{ item.id }}
      </template>

      <template v-slot:item.created="{ item }">
        {{
          isToday(new Date(item.created))
            ? new Date(item.created)
                .toLocaleTimeString()
                .split(" ")[0]
                .replace(/:[0-9]*$/, "")
            : new Date(item.created).toLocaleDateString().replace(/ /g, "")
        }}
      </template>

      <template v-slot:item.customer.address.raw="{ item }">
        <v-icon v-if="item.deliveryMethod === 'delivery'" small class="mr-1"
          >mdi-truck-delivery</v-icon
        >
        <v-icon
          v-else-if="item.deliveryMethod === 'takeaway'"
          small
          class="mr-1"
          >mdi-walk</v-icon
        >
        <v-icon v-else-if="item.deliveryMethod === 'inplace'" small class="mr-1"
          >mdi-silverware-fork-knife</v-icon
        >
        <span
          v-if="
            item.deliveryMethod === 'inplace' && !item.deliveryDetails.tableId
          "
        >
          {{
            $t("inPlaceAt", {
              time: item.deliveryDetails.time,
              persons: item.deliveryDetails.persons,
            })
          }}</span
        >
        <span
          v-else-if="
            item.deliveryMethod === 'inplace' && item.deliveryDetails.tableId
          "
        >
          {{
            $t("inPlaceTable", { table: item.deliveryDetails.tableId })
          }}</span
        >
        <span v-else>
          {{ item.customer.address.raw }}
        </span>
      </template>

      <template v-slot:item.scheduledAt="{ item }">
        {{
          item.scheduledAt
            ? scheduledAtToText(item.scheduledAt, item.created)
            : ""
        }}
      </template>

      <template v-slot:item.total="{ item }"
        >{{ item.total.toFixed(2) }} {{ currencySymbol }}</template
      >

      <template v-slot:item.payment="{ item }">
        <v-chip v-if="!item.payment.onlinePayment" small
          >{{ $t("cod") }}
          {{
            getCashOnDeliveryType(item)
              ? `(${$t(getCashOnDeliveryType(item))})`
              : ""
          }}</v-chip
        >
        <v-chip
          v-else-if="
            item.payment.onlinePayment && item.payment.status === 'pending'
          "
          small
          >{{ $t("waitingForPayment") }}</v-chip
        >
        <v-chip
          v-else-if="
            item.payment.onlinePayment && item.payment.status === 'paid'
          "
          small
          color="success"
          >{{ $t("paid") }}</v-chip
        >
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip v-if="item.status === 'pending'" small>{{
          $t("pending")
        }}</v-chip>
        <v-chip v-if="item.status === 'confirmed'" small color="success">{{
          $t("confirmed")
        }}</v-chip>
        <v-chip v-if="item.status === 'rejected'" small color="error">{{
          $t("rejected")
        }}</v-chip>
        <v-chip v-if="item.status === 'completed'" small color="primary">{{
          $t("completed")
        }}</v-chip>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td id="edpandedTd" :colspan="headers.length">
          <v-data-table
            id="expandedTable"
            class="mb-5"
            :headers="orderItemsHeaders"
            :header-props="{ sortByText: $t('sortBy') }"
            :items="item.items"
            item-key="_id"
            hide-default-footer
            disable-pagination
            :style="$vuetify.breakpoint.xs ? 'width: 95%; margin: 0 auto' : ''"
          >
            <template v-slot:item.total="{ item }"
              >{{ item.total.toFixed(2) }} {{ currencySymbol }}</template
            >
            <template v-slot:item.price="{ item }"
              >{{ item.price.toFixed(2) }} {{ currencySymbol }}</template
            >
            <template v-slot:body.append="{ headers, items }">
              <tr
                v-if="
                  item.discount &&
                  item.discount.amount > 0 &&
                  $vuetify.breakpoint.xs
                "
              >
                <td
                  style="border-bottom: 1px solid rgba(0, 0, 0, 0.12)"
                  :colspan="headers.length"
                  class="d-flex justify-space-between py-3"
                >
                  <b>{{ $t(`${item.discount.description}`) }}</b>
                  <span
                    >-{{ item.discount.amount.toFixed(2) }}
                    {{ currencySymbol }}</span
                  >
                </td>
              </tr>
              <tr
                v-else-if="item.discount && item.discount.amount > 0"
                style="border-bottom: none"
              >
                <td
                  style="
                    border-top: 1px solid rgba(0, 0, 0, 0.12);
                    border-bottom: none;
                  "
                  :colspan="headers.length - 1"
                >
                  <span>{{ $t(`${item.discount.description}`) }}</span>
                </td>
                <td
                  style="
                    border-top: 1px solid rgba(0, 0, 0, 0.12);
                    border-bottom: none;
                  "
                >
                  <span
                    >-{{ item.discount.amount.toFixed(2) }}
                    {{ currencySymbol }}</span
                  >
                </td>
              </tr>

              <tr v-if="$vuetify.breakpoint.xs">
                <td
                  style="
                    border-top: 1px solid rgba(0, 0, 0, 0.12);
                    border-bottom: none;
                  "
                  :colspan="headers.length"
                  class="d-flex justify-space-between py-3"
                >
                  <b>{{ $t("total") }}</b>
                  <span>{{
                    item.deliveryMethod === "delivery"
                      ? ` (${$t("withDelivery")} ${item.deliveryPrice.toFixed(
                          2
                        )} ${currencySymbol})`
                      : ""
                  }}</span>
                  <b>{{ item.total.toFixed(2) }} {{ currencySymbol }}</b>
                </td>
              </tr>
              <tr v-else style="border-bottom: none">
                <td
                  style="
                    border-top: 1px solid rgba(0, 0, 0, 0.12);
                    border-bottom: none;
                  "
                  :colspan="headers.length - 1"
                >
                  <b>{{ $t("total") }}</b>
                  <span>{{
                    item.deliveryMethod === "delivery"
                      ? ` (${$t("withDelivery")} ${item.deliveryPrice.toFixed(
                          2
                        )} ${currencySymbol})`
                      : ""
                  }}</span>
                </td>
                <td
                  style="
                    border-top: 1px solid rgba(0, 0, 0, 0.12);
                    border-bottom: none;
                  "
                >
                  <b>{{ item.total.toFixed(2) }} {{ currencySymbol }}</b>
                </td>
              </tr>
            </template>
          </v-data-table>
          <div style="width: 95%; margin: 0 auto; margin-bottom: 20px">
            <v-dialog v-model="dialog" max-width="600" persistent>
              <v-card>
                <v-card-title class="headline">{{ dialogTitle }}</v-card-title>
                <v-card-text>
                  <v-container>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="computedSubject"
                          :label="$t('subject')"
                          placeholder="Your order is on the way"
                          rounded
                          filled
                          hide-details
                        ></v-text-field>
                      </v-col>
                      <v-col
                        v-if="confirmationDialog === true"
                        cols="12"
                        sm="6"
                      >
                        <v-text-field
                          v-model="editedDialog.deliveryTime"
                          type="string"
                          :label="$t('deliveryTime')"
                          placeholder="25 minutes"
                          rounded
                          filled
                          hide-details
                        ></v-text-field>
                      </v-col>
                      <v-col
                        v-if="confirmationDialog === true"
                        cols="12"
                        sm="6"
                      >
                        <v-text-field
                          v-model="editedDialog.totalWithDelivery"
                          type="number"
                          :label="$t('total')"
                          :hint="`${$t('orderTotalIs', {
                            total: item.total,
                          })} ${currencySymbol}`"
                          persistent-hint
                          rounded
                          filled
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="computedMessage"
                          filled
                          rounded
                          :label="$t('message')"
                          auto-grow
                          hide-details
                        ></v-textarea>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text rounded @click="close">{{
                    $t("close")
                  }}</v-btn>
                  <v-btn
                    v-if="confirmationDialog === false"
                    color="error"
                    text
                    rounded
                    @click="
                      rejectOrder(item._id, computedSubject, computedMessage)
                    "
                    >{{ $t("rejectOrder") }}</v-btn
                  >
                  <v-btn
                    v-if="confirmationDialog === true"
                    color="success"
                    text
                    rounded
                    @click="
                      confirmOrder(item._id, computedSubject, computedMessage)
                    "
                    >{{ $t("confirmOrder") }}</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-row>
              <v-expansion-panels
                multiple
                style="margin-bottom: 20px"
                :value="panel"
                class="justify-start"
              >
                <v-col cols="12" md="6">
                  <v-expansion-panel :disabled="!item.note">
                    <v-expansion-panel-header>{{
                      $t("note")
                    }}</v-expansion-panel-header>
                    <v-expansion-panel-content>{{
                      item.note ? item.note : $t("noteNotProvided")
                    }}</v-expansion-panel-content>
                  </v-expansion-panel>

                  <v-expansion-panel>
                    <v-expansion-panel-header>{{
                      $t("detailedCustomerInfo")
                    }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-card
                        class="mx-auto"
                        flat
                        rounded
                        color="rgba(0, 0, 0, 0.06)"
                        style="border-radius: 28px !important"
                      >
                        <v-list-item three-line>
                          <v-list-item-avatar color="#0360ec">
                            <span class="white--text">{{
                              item.customer.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                            }}</span>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title class="">{{
                              item.customer.name
                            }}</v-list-item-title>
                            <v-list-item-subtitle>{{
                              item.customer.email
                            }}</v-list-item-subtitle>
                            <v-list-item-subtitle class="mb-2">{{
                              item.customer.mobile
                            }}</v-list-item-subtitle>
                            <v-list-item-subtitle v-if="item.customer.address"
                              >{{ item.customer.address.street }},
                              {{ item.customer.address.zip }}
                              {{
                                item.customer.address.city
                              }}</v-list-item-subtitle
                            >
                            <v-list-item-subtitle v-if="item.customer.ip"
                              >{{ item.customer.ip.ipAddress }} ({{
                                item.customer.ip.ipAddressLocation
                              }})</v-list-item-subtitle
                            >
                          </v-list-item-content>
                        </v-list-item>
                      </v-card>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-col>

                <v-col cols="12" md="6">
                  <v-expansion-panel>
                    <v-expansion-panel-header>{{
                      $t("activity")
                    }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <OrderActivity
                        :createdAt="item.created"
                        :activity="item.activity"
                        :sourceUrl="item.sourceUrl"
                      ></OrderActivity>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-col>
              </v-expansion-panels>
            </v-row>

            <v-btn
              v-if="item.status === 'pending'"
              color="success"
              text
              rounded
              @click="
                dialog = true;
                confirmationDialog = true;
                editedDialog.totalWithDelivery = item.total;
              "
              >{{
                $vuetify.breakpoint.xs ? $t("confirm") : $t("confirmOrder")
              }}</v-btn
            >
            <v-btn
              v-if="item.status === 'pending'"
              color="error"
              text
              rounded
              @click="
                dialog = true;
                confirmationDialog = false;
              "
              >{{
                $vuetify.breakpoint.xs ? $t("reject") : $t("rejectOrder")
              }}</v-btn
            >
            <v-btn
              v-if="item.status === 'confirmed'"
              color="primary"
              text
              rounded
              @click="completeOrder(item._id)"
              >{{ $t("completeOrder") }}</v-btn
            >
          </div>
        </td>
      </template>
      <template v-slot:no-data>
        {{ $t("noData") }}
      </template>
      <template v-slot:no-results>
        {{ $t("noData") }}
      </template>
    </v-data-table>
    <v-snackbar v-model="snackbar" :timeout="timeout">
      {{ snackbarText }}
      <v-btn text small rounded @click="stopAudio()">Ok</v-btn>
    </v-snackbar>
  </v-skeleton-loader>
</template>

<script>
import restaurantService from "../services/restaurant.service";
import orderService from "../services/order.service";
import { API_URL } from "../api.js";

import OrderActivity from "../components/OrderActivity";

export default {
  props: ["searchValue"],
  components: {
    OrderActivity,
  },
  data() {
    return {
      selection: "pending",
      loading: true,
      dialog: false,
      snackbar: false,
      snackbarText: "",
      timeout: 0,
      confirmationDialog: Boolean,
      expanded: [],
      headers: [
        { text: this.$t("id"), value: "id", sortable: false },
        { text: this.$t("created"), value: "created" },
        {
          text: this.$t("deliveryMethod"),
          value: "customer.address.raw",
          sortable: false,
        },
        { text: this.$t("schedule"), value: "scheduledAt", sortable: false },
        { text: this.$t("customer"), value: "customer.email", sortable: false },
        { text: this.$t("mobile"), value: "customer.mobile", sortable: false },
        { text: this.$t("total"), value: "total" },
        { text: this.$t("payment"), value: "payment", sortable: false },
        { text: this.$t("status"), value: "status", sortable: false },
        { text: "", value: "data-table-expand" },
      ],
      orderItemsHeaders: [
        { text: this.$t("id"), value: "id" },
        { text: this.$t("name"), value: "name" },
        { text: this.$t("options"), value: "options" },
        { text: this.$t("price"), value: "price" },
        { text: this.$t("quantity"), value: "quantity" },
        { text: this.$t("total"), value: "total" },
      ],
      items: [],
      panel: [0],
      defaultMessages: {
        confirmation: {
          subject: "",
          message: "",
        },
        rejection: {
          subject: "",
          message: "",
        },
      },
      defaultDialog: {
        subject: null,
        deliveryTime: "",
        totalWithDelivery: null,
        message: null,
      },
      editedDialog: {
        subject: null,
        deliveryTime: "",
        totalWithDelivery: null,
        message: null,
      },
      sse: new EventSource(
        `${API_URL}/order/stream/${localStorage.getItem(
          "restaurantId"
        )}?token=${localStorage.getItem("token")}`,
        { withCredentials: true }
      ),
      audio: new Audio(require("../assets/notification.mp3")),
    };
  },
  watch: {
    selection(v) {
      if (!v) {
        this.selection = "any";
      }
    },
  },
  computed: {
    dialogTitle() {
      return this.confirmationDialog === true
        ? this.$t("confirmOrder")
        : this.$t("rejectOrder");
    },
    computedMessage: {
      get: function () {
        if (this.confirmationDialog) {
          if (this.editedDialog.message !== null) {
            let copy = this.editedDialog.message;
            if (this.editedDialog.deliveryTime)
              copy = copy.replace(
                "DELIVERY_TIME",
                this.editedDialog.deliveryTime
              );
            if (this.editedDialog.totalWithDelivery)
              copy = copy.replace(
                "TOTAL_WITH_DELIVERY",
                this.editedDialog.totalWithDelivery
              );
            return copy;
          } else {
            let copy = this.defaultMessages.confirmation.message;
            if (this.editedDialog.deliveryTime)
              copy = copy.replace(
                "DELIVERY_TIME",
                this.editedDialog.deliveryTime
              );
            if (this.editedDialog.totalWithDelivery)
              copy = copy.replace(
                "TOTAL_WITH_DELIVERY",
                this.editedDialog.totalWithDelivery
              );
            return copy;
          }
        } else {
          if (this.editedDialog.message !== null)
            return this.editedDialog.message;
          else return this.defaultMessages.rejection.message;
        }
      },
      set: function (newValue) {
        if (this.confirmationDialog) {
          let copy = newValue;
          if (this.editedDialog.deliveryTime)
            copy = copy.replace(
              this.editedDialog.deliveryTime,
              "DELIVERY_TIME"
            );
          if (this.editedDialog.totalWithDelivery)
            copy = copy.replace(
              this.editedDialog.totalWithDelivery,
              "TOTAL_WITH_DELIVERY"
            );
          return (this.editedDialog.message = copy);
        } else return (this.editedDialog.message = newValue);
      },
    },
    computedSubject: {
      get: function () {
        if (this.confirmationDialog) {
          if (this.editedDialog.subject !== null)
            return this.editedDialog.subject;
          else return this.defaultMessages.confirmation.subject;
        } else {
          if (this.editedDialog.subject !== null)
            return this.editedDialog.subject;
          else return this.defaultMessages.rejection.subject;
        }
      },
      set: function (newValue) {
        return (this.editedDialog.subject = newValue);
      },
    },
    currencySymbol() {
      return localStorage.currencySymbol;
    },
  },

  created() {
    this.initialize();
    this.initializeSse();
  },

  beforeDestroy() {
    try {
      clearInterval(this.fetchPendingInterval);
      this.sse.close();
      this.stopAudio();
    } finally {
      console.log("Closing SSE connection and stoping audio...");
    }
  },

  methods: {
    initializeSse() {
      this.sse.onmessage = (e) => {
        console.log("SSE message");
        const data = JSON.parse(e.data);
        if (Array.isArray(data)) this.items = data;
        else {
          const index = this.items.findIndex((item) => item._id === data._id);
          if (index === -1) {
            this.items.push(data);
            this.snackbarText = this.$t("newOrderNumberWorth", {
              id: data.id,
              total: data.total,
              currency: this.currencySymbol,
            });
            this.playAudio();
          } else {
            if (
              this.items[index].payment.onlinePayment &&
              this.items[index].payment.status !== data.payment.status
            ) {
              this.items[index].payment = data.payment;
              this.snackbarText = this.$t("paymentForOrderNumberReceived", {
                id: data.id,
                total: data.total,
                currency: this.currencySymbol,
              });
              this.playAudio();
            } else {
              this.items[index].status = data.status;
              this.items[index].activity = data.activity;
            }
          }
        }
      };
      this.sse.onerror = (e) => {
        console.log(e);
      };
    },
    async initialize() {
      const orders = await orderService.getOrders(
        localStorage.getItem("restaurantId"),
        null,
        this.$route.query.includeOrderId
      );
      this.items = orders;
      this.loading = false;

      this.fetchPendingInterval = setInterval(
        async function () {
          const orders = await orderService.getOrders(
            localStorage.getItem("restaurantId"),
            "pending"
          );
          orders.forEach((order) => {
            const index = this.items.findIndex(
              (item) => item._id === order._id
            );
            if (index === -1) {
              this.items.push(order);
              this.snackbarText = this.$t("newOrderNumberWorth", {
                id: order.id,
                total: order.total,
                currency: this.currencySymbol,
              });
              this.playAudio();
            }
          });
        }.bind(this),
        300000
      );

      const resp = await restaurantService.getRestaurant(
        localStorage.getItem("restaurantId"),
        "defaultMessages"
      );
      this.defaultMessages.confirmation.subject = resp.confirmation.subject;
      this.defaultMessages.confirmation.message = resp.confirmation.message;
      this.defaultMessages.rejection.subject = resp.rejection.subject;
      this.defaultMessages.rejection.message = resp.rejection.message;
    },

    customFilter(value, search, item) {
      if (search != "any")
        return (
          value != null &&
          search != null &&
          item.status != null &&
          item.status === search &&
          (this.searchValue === "" ||
            item.id.includes(this.searchValue) ||
            item.customer.email.includes(this.searchValue))
        );
      return (
        item.id.includes(this.searchValue) ||
        item.customer.email.includes(this.searchValue)
      );
    },

    async confirmOrder(orderId, subject, message) {
      if (window.confirm(this.$t("areYouSureYouWantToConfirmThisOrder"))) {
        const resp = await orderService.confirmOrder(
          localStorage.getItem("restaurantId"),
          orderId,
          subject,
          message
        );
        const index = this.items.findIndex((item) => item._id === resp._id);
        this.items[index].status = resp.status;
        this.items[index].activity = resp.activity;
        this.close();
      }
    },

    async rejectOrder(orderId, subject, message) {
      if (window.confirm(this.$t("areYouSureYouWantToRejectThisOrder"))) {
        const resp = await orderService.rejectOrder(
          localStorage.getItem("restaurantId"),
          orderId,
          subject,
          message
        );
        const index = this.items.findIndex((item) => item._id === resp._id);
        this.items[index].status = resp.status;
        this.items[index].activity = resp.activity;
        this.close();
      }
    },
    async completeOrder(orderId) {
      if (window.confirm(this.$t("areYouSureYouWantToCompleteThisOrder"))) {
        const resp = await orderService.completeOrder(
          localStorage.getItem("restaurantId"),
          orderId
        );
        const index = this.items.findIndex((item) => item._id === resp._id);
        this.items[index].status = resp.status;
        this.items[index].activity = resp.activity;
      }
    },

    playAudio() {
      this.snackbar = true;
      this.audio.pause();
      this.audio.loop = true;
      this.audio.play();
    },

    stopAudio() {
      this.snackbar = false;
      this.audio.loop = false;
      this.audio.pause();
    },

    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedDialog = Object.assign({}, this.defaultDialog);
      }, 300);
    },

    scheduledAtToText(scheduledAt, created) {
      const scheduledAtDate = new Date(scheduledAt);
      const difference = Math.abs(scheduledAtDate.getTime() - created);
      const scheduledAtTime = `${("0" + scheduledAtDate.getHours()).slice(
        -2
      )}:${("0" + scheduledAtDate.getMinutes()).slice(-2)}`;

      const getDodaysMorningMilis = () => {
        const date = new Date();
        date.setMilliseconds(0);
        date.setSeconds(0);
        date.setMinutes(0);
        date.setHours(0);
        return date.getTime();
      };

      if (scheduledAtDate.getTime() < getDodaysMorningMilis())
        return this.$t("scheduledAt", {
          day: scheduledAtDate.toLocaleDateString().replace(/ /g, ""),
          time: difference <= 1000 ? this.$t("asap") : scheduledAtTime,
        });

      if (difference <= 1000) return this.$t("asap");

      return this.$t("scheduledAt", {
        day:
          new Date().setHours(0, 0, 0, 0) ===
          new Date(scheduledAt).setHours(0, 0, 0, 0)
            ? this.$t("today") +
              ` (${scheduledAtDate.toLocaleDateString().replace(/ /g, "")})`
            : this.$t("tomorrow") +
              ` (${scheduledAtDate.toLocaleDateString().replace(/ /g, "")})`,
        time: scheduledAtTime,
      });
    },

    isToday(date) {
      const today = new Date();
      return (
        date.getDate() == today.getDate() &&
        date.getMonth() == today.getMonth() &&
        date.getFullYear() == today.getFullYear()
      );
    },

    getCashOnDeliveryType(item) {
      try {
        return item.deliveryDetails.cashOnDeliveryType;
      } catch {
        return null;
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    try {
      this.sse.close();
    } finally {
      next();
    }
  },
};
</script>

<style>
.v-data-table tbody tr.expanded.expanded__content {
  box-shadow: none !important;
}

.v-data-table tbody tr.v-data-table__expanded__content {
  -webkit-box-shadow: none !important;
  box-shadow: none !important;
}

@media (min-width: 600px) {
  #expandedTable tbody tr td {
    border-bottom: none;
  }
}

.theme--light.v-data-table tbody tr.expanded.expanded__content:hover,
#edpandedTd * :hover {
  background: none;
}
</style>
